import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { useOutings } from '../hooks/useOutings'
import { useRoundHistory } from '../hooks/useRoundHistory'
import type { HistoryPlayer, HistoryRound } from '../hooks/useRoundHistory'
import {
  AreaSectionHead,
  ClubButton,
  ClubLinkButton,
  ClubPage,
  Container,
  EmptyPlate,
  PageHead,
  Scorecard,
  SignInPrompt,
} from '../components/golf'
import type { PageStat, ScorecardColumn, ScorecardRow } from '../components/golf'
import { cn } from '../lib/cn'
import { formatCents } from '../lib/clubMoney'
import { MAX_INVITES, contactFromLine } from '../lib/inviteContacts'
import type { Rebooking, RebookingInvite } from '../hooks/useOutings'
import type { InviteContact, Outing } from '../lib/types'

/**
 * The Record (`/clubhouse/record`) — the clubhouse looking backwards.
 *
 * Two things a member asks about golf they have already played: what did we
 * shoot, and can we do that again. This page answers both on one surface,
 * because they are one question — the outing IS the thing you re-book, and the
 * round is how you remember which outing was worth repeating.
 *
 * It joins two reads that already existed rather than a history endpoint of
 * its own: `GET /golf/rounds` (every round you kept or played in, with the
 * whole card) and `GET /golf/outings?scope=past`. `round.outing_id` is the
 * join, and it has been on the payload since rounds were built.
 *
 * THE HONEST-EMPTY RULE IS THE POINT OF HALF THIS FILE. A round with no cells
 * says so; a course with no seeded pars gets no par row rather than a made-up
 * one; a player who never accepted is labelled, not counted. Nothing here
 * fills a gap with a plausible number
 * (docs/fireside/club-content-inventory.md).
 *
 * Re-booking is a DRAFT, never a write: `GET /golf/outings/:id/rebooking`
 * composes the next outing from a past one, the member edits every line of it,
 * and it is proposed through the ordinary create. Nobody is asked anything
 * until the one flag CTA at the bottom of the rail is pressed.
 */

/** "Saturday, 8 August" — how the record dates a round. */
function formatPlayedOn(iso: string | null): string {
  if (!iso) return 'Date not recorded'
  const date = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date)
}

/** "Sat, Aug 8 · 12:20 PM" — a tee time, the same shape the outings desk uses. */
function formatTeeTime(iso: string | null): string {
  if (!iso) return 'No tee time'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const day = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date)
  const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date)
  return `${day} · ${time}`
}

/**
 * An ISO instant as a `datetime-local` value, in the reader's own clock.
 *
 * Never `toISOString().slice(0,16)` — that renders UTC, so a 7:10 AM Eastern
 * tee time arrives in the field as 11:10 and the member proposes an afternoon
 * round without noticing.
 */
function toDateTimeLocal(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

const inputClass =
  'w-full rounded-club border border-ink/20 bg-parchment px-[13px] py-[9px] text-[13.5px] text-ink placeholder:text-ink-soft focus:border-fairway focus:shadow-[0_0_0_3px_rgba(44,90,65,0.14)]'

const labelClass =
  'mb-[6px] block text-[10px] font-semibold uppercase tracking-[0.16em] text-brass-deep'

// ── The card ────────────────────────────────────────────────────────────────

/** The holes this round played, split into the nine out and the nine in. */
function nines(round: HistoryRound): { out: number[]; back: number[] } {
  const holes = round.hole_numbers
  return holes.length > 9
    ? { out: holes.slice(0, 9), back: holes.slice(9) }
    : { out: holes, back: [] }
}

/** A player's strokes over a set of holes, and null when none were entered. */
function strokesOver(player: HistoryPlayer, holes: number[]): number | null {
  const cells = player.scores.filter((score) => holes.includes(score.hole_number))
  if (cells.length === 0) return null
  return cells.reduce((sum, score) => sum + score.strokes, 0)
}

/** A par total over a set of holes — null unless every one of them is carded. */
function parOver(round: HistoryRound, holes: number[]): number | null {
  const pars = holes.map((hole) => round.course.holes.find((h) => h.number === hole)?.par ?? null)
  if (pars.length === 0 || pars.some((par) => par == null)) return null
  return pars.reduce((sum: number, par) => sum + (par as number), 0)
}

/** Whether this round's course carries pars at all — an unseeded one does not. */
function hasPars(round: HistoryRound): boolean {
  return round.course.holes.some((hole) => hole.par != null)
}

/**
 * The card, as the club's standings table: a column per hole the round
 * actually played, then Out / In / Total. Nine-hole rounds get Total alone —
 * there is no "in" to report and a blank column would only invite the question.
 */
function RoundScorecard({ round, viewerId }: { round: HistoryRound; viewerId: string | null }) {
  const { out, back } = nines(round)
  const columns: ScorecardColumn[] = [
    { key: 'name', label: 'Hole' },
    ...round.hole_numbers.map((hole) => ({ key: `h${hole}`, label: String(hole), numeric: true })),
    ...(back.length > 0
      ? [
          { key: 'out', label: 'Out', numeric: true },
          { key: 'in', label: 'In', numeric: true },
        ]
      : []),
    { key: 'total', label: 'Total', numeric: true },
  ]

  const holeCells = (value: (hole: number) => React.ReactNode) =>
    Object.fromEntries(round.hole_numbers.map((hole) => [`h${hole}`, value(hole)]))

  const rows: ScorecardRow[] = []

  if (hasPars(round)) {
    const parTotal = parOver(round, round.hole_numbers)
    rows.push({
      id: 'par',
      cells: {
        name: 'Par',
        ...holeCells((hole) => round.course.holes.find((h) => h.number === hole)?.par ?? '—'),
        ...(back.length > 0 ? { out: parOver(round, out) ?? '—', in: parOver(round, back) ?? '—' } : {}),
        total: parTotal ?? '—',
      },
    })
  }

  for (const player of round.players) {
    rows.push({
      id: player.id,
      isYou: player.user_id != null && player.user_id === viewerId,
      cells: {
        name: player.display_name,
        ...holeCells(
          (hole) => player.scores.find((score) => score.hole_number === hole)?.strokes ?? '—',
        ),
        ...(back.length > 0
          ? { out: strokesOver(player, out) ?? '—', in: strokesOver(player, back) ?? '—' }
          : {}),
        total: player.total_strokes ?? '—',
      },
    })
  }

  return (
    // Eighteen holes plus totals will not fit a phone, and the house rule is
    // wrap-don't-hide — a card cannot wrap, so it scrolls in its own box
    // rather than pushing the page sideways.
    <div className="mt-4 overflow-x-auto border-t border-brass/25 pt-4">
      <Scorecard
        caption={`The card — ${round.course.name}, ${formatPlayedOn(round.played_on)}`}
        columns={columns}
        rows={rows}
        className="min-w-[640px]"
      />
    </div>
  )
}

// ── One round in the record ─────────────────────────────────────────────────

/** What a player's line says about them beyond their name. */
function playerTag(player: HistoryPlayer): string | null {
  if (player.guest) return 'Guest'
  if (player.declined_at) return 'Declined'
  if (!player.accepted_at) return 'Not answered'
  return null
}

function PlayerChip({ player, isYou }: { player: HistoryPlayer; isYou: boolean }) {
  const tag = playerTag(player)
  return (
    <li
      className={cn(
        'inline-flex items-center gap-2 rounded-club-sm border border-sage-soft px-2.5 py-1.5 text-[12.5px] text-ink',
        isYou ? 'bg-sage-soft' : 'bg-paper',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid size-[18px] place-items-center rounded-full font-display text-[9px]',
          player.guest
            ? 'border border-dashed border-sage text-ink-soft'
            : 'bg-pine text-paper',
        )}
      >
        {player.display_name.trim().charAt(0).toUpperCase() || '·'}
      </span>
      {player.display_name}
      {player.total_strokes != null && (
        <b className="font-display text-[15px] font-medium tabular-nums text-pine">
          {player.total_strokes}
        </b>
      )}
      {tag && (
        <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-brass-deep">
          {tag}
        </span>
      )}
    </li>
  )
}

/** The one-line summary beside a round's course name. */
function roundMeta(round: HistoryRound): string {
  const parts = [`${round.holes_count} holes`]
  if (round.starting_hole !== 1) parts.push(`from the ${round.starting_hole}th`)
  if (round.course.par != null) parts.push(`Par ${round.course.par}`)
  if (round.status === 'in_progress') parts.push('Still out there')
  else parts.push(round.posted_at ? 'Posted to the club' : 'Not posted')
  return parts.join(' · ')
}

function RoundEntry({
  round,
  outing,
  viewerId,
  open,
  onToggle,
  onRebook,
}: {
  round: HistoryRound
  /** The outing it came out of, when the member still has that outing. */
  outing: Outing | undefined
  viewerId: string | null
  open: boolean
  onToggle: () => void
  onRebook: (outingId: string) => void
}) {
  const carded = round.players.some((player) => player.scores.length > 0)

  return (
    <li
      className={cn(
        'rounded-[6px] border bg-parchment px-[22px] pb-4 pt-5',
        open ? 'border-fairway' : 'border-ink/12',
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div>
          <p className="font-display text-[12.5px] text-brass-deep">
            {formatPlayedOn(round.played_on)}
          </p>
          <h3 className="font-display text-[22px] font-medium text-pine">{round.course.name}</h3>
        </div>
        <span className="text-[12.5px] tabular-nums text-ink-soft">{roundMeta(round)}</span>
      </div>

      <ul className="mt-3.5 flex flex-wrap gap-2">
        {round.players.map((player) => (
          <PlayerChip
            key={player.id}
            player={player}
            isYou={player.user_id != null && player.user_id === viewerId}
          />
        ))}
      </ul>

      {/* The honest empty. Golf that happened with nothing written down says
          exactly that; it does not borrow a card from anywhere. */}
      {!carded && (
        <p className="mt-3.5 rounded-club-sm border border-dashed border-brass bg-paper px-3.5 py-3 text-[13px] text-ink-soft">
          <b className="font-semibold text-ink">No card was kept for this round.</b> Nobody wrote
          the scores down, and the club does not invent them.
        </p>
      )}

      {carded && open && <RoundScorecard round={round} viewerId={viewerId} />}

      <div className="mt-3.5 flex flex-wrap items-center gap-4 border-t border-sage-soft pt-3">
        {outing && (
          <ClubButton size="sm" variant="ink" onClick={() => onRebook(outing.id)}>
            Book it again
          </ClubButton>
        )}
        {carded && (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="rounded-club-sm text-[13px] text-fairway underline underline-offset-[3px]"
          >
            {open ? 'Fold the card' : 'Open the card'}
          </button>
        )}
        <span className="ml-auto text-[12px] text-ink-soft">
          {/* Three states, not two. A round can name an outing this page did
              not load — the outings read is scoped to the past, and a round
              played early sits under one that has not teed off yet. Saying
              "no outing" there would be a plain untruth. */}
          {outing
            ? `From the outing of ${formatTeeTime(outing.tee_off_at)}`
            : round.outing_id
              ? 'From an outing still on the book'
              : 'No outing — a round on the day'}
        </span>
      </div>
    </li>
  )
}

// ── One past outing ─────────────────────────────────────────────────────────

const INVITE_STATE: Record<string, string> = {
  accepted: 'in',
  declined: 'declined',
  invited: 'never answered',
}

function PastOutingRow({
  outing,
  onRebook,
}: {
  outing: Outing
  onRebook: (outingId: string) => void
}) {
  return (
    <li className="rounded-[6px] border border-ink/12 bg-parchment px-[18px] py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div>
          <p className="font-display text-[12.5px] text-brass-deep">
            {formatTeeTime(outing.tee_off_at)}
          </p>
          <h3 className="font-display text-[18px] font-medium text-pine">
            {outing.course_name}
            {outing.holes != null && <> · {outing.holes} holes</>}
            {outing.price_cents_per_head != null && (
              <> · {formatCents(outing.price_cents_per_head)} a head</>
            )}
          </h3>
        </div>
        <span
          className={cn(
            'whitespace-nowrap rounded-club-sm border px-2 py-1 text-[9.5px] font-semibold uppercase tracking-[0.14em]',
            outing.status === 'booked'
              ? 'border-sage text-fairway'
              : 'border-brass text-brass-deep',
          )}
        >
          {outing.status}
          {outing.booking_ref ? ` · ref ${outing.booking_ref}` : ''}
        </span>
      </div>

      {outing.invites.length > 0 ? (
        <ul className="mt-2.5 flex flex-wrap gap-1.5">
          {outing.invites.map((invite) => (
            <li
              key={invite.contact}
              className="rounded-club-sm border border-sage-soft bg-paper px-2 py-1 text-[12px] text-ink-soft"
            >
              {invite.contact} — {INVITE_STATE[invite.status] ?? invite.status}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2.5 text-[12px] text-ink-soft">Nobody was invited to this one.</p>
      )}

      <div className="mt-3 border-t border-sage-soft pt-3">
        <ClubButton size="sm" variant="ink" onClick={() => onRebook(outing.id)}>
          Book it again
        </ClubButton>
      </div>
    </li>
  )
}

// ── The composer ────────────────────────────────────────────────────────────

/** What the composer holds while the member edits it. */
interface Draft {
  teeOffAt: string
  holes: number
  slots: number
  /** Dollars, as typed. Empty means the member cleared the rate. */
  price: string
  invites: RebookingInvite[]
}

function draftFrom(rebooking: Rebooking): Draft {
  return {
    teeOffAt: toDateTimeLocal(rebooking.tee_off_at),
    holes: rebooking.holes ?? 18,
    slots: rebooking.slots,
    price:
      rebooking.price_cents_per_head == null
        ? ''
        : String(rebooking.price_cents_per_head / 100),
    invites: rebooking.invites,
  }
}

const PREVIOUS_ANSWER: Record<string, string> = {
  accepted: 'In last time',
  declined: 'Declined last time',
  invited: 'Never answered',
}

/**
 * A past outing, opened as a new one.
 *
 * EVERY line is editable, which is the whole feature: the reason to re-book is
 * usually that something has to change — a different hour, one fewer player, a
 * friend who could not make the last one. The values it opens with are the
 * club's suggestion and nothing more, and the two that are guesses say what
 * they were guessed from right underneath.
 */
function RebookComposer({
  rebooking,
  onCancel,
  onPropose,
}: {
  rebooking: Rebooking
  onCancel: () => void
  onPropose: (body: {
    course_key: string
    tee_off_at: string
    slots: number
    holes: number
    price_cents_per_head: number
    invites: InviteContact[]
  }) => Promise<void>
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(rebooking))
  const [added, setAdded] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  const dropInvite = (contact: string) =>
    setDraft((prev) => ({
      ...prev,
      invites: prev.invites.filter((invite) => invite.contact !== contact),
    }))

  const addInvite = () => {
    const contact = contactFromLine(added)
    if (!contact) {
      setError(`"${added.trim()}" is neither an email address nor a phone number.`)
      return
    }
    const value = contact.email ?? contact.phone ?? ''
    if (draft.invites.some((invite) => invite.contact === value)) {
      setError(`${value} is already on this invitation.`)
      return
    }
    if (draft.invites.length >= MAX_INVITES) {
      setError(`An outing takes at most ${MAX_INVITES} invitations.`)
      return
    }
    setError(null)
    setAdded('')
    setDraft((prev) => ({
      ...prev,
      invites: [
        ...prev.invites,
        { email: contact.email ?? null, phone: contact.phone ?? null, contact: value, previous_status: null },
      ],
    }))
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!draft.teeOffAt) {
      setError('Give the outing a tee time before proposing it.')
      return
    }
    const teeOff = new Date(draft.teeOffAt)
    if (Number.isNaN(teeOff.getTime())) {
      setError('That tee time could not be read — pick it again.')
      return
    }
    const dollars = Number(draft.price)
    if (draft.price !== '' && (Number.isNaN(dollars) || dollars < 0)) {
      setError('The green fee has to be a number, or nothing at all.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onPropose({
        course_key: rebooking.course_key,
        tee_off_at: teeOff.toISOString(),
        slots: draft.slots,
        holes: draft.holes,
        price_cents_per_head: Math.round((draft.price === '' ? 0 : dollars) * 100),
        invites: draft.invites.map((invite) =>
          invite.email ? { email: invite.email } : { phone: invite.phone as string },
        ),
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The outing could not be proposed — try again.')
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={submit}
      aria-label="Propose this outing again"
      className="rounded-[6px] border border-brass/55 bg-parchment p-5"
    >
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-brass-deep">
        The same again
      </p>
      <h2 className="mt-1.5 font-display text-[20px] font-medium text-pine">
        {rebooking.course_name}
      </h2>
      <p className="mb-4 mt-1 text-[12px] text-ink-soft">
        Drawn from your outing of {formatTeeTime(rebooking.source_tee_off_at)}. Nothing is sent
        until you say so, and every line below can be changed first.
      </p>

      <div className="mb-3.5">
        <label htmlFor="rebook-tee" className={labelClass}>
          Tee
        </label>
        <input
          id="rebook-tee"
          type="datetime-local"
          value={draft.teeOffAt}
          onChange={(e) => set('teeOffAt', e.target.value)}
          className={inputClass}
        />
        <p className="mt-1.5 text-[11px] text-ink-soft">
          The same weekday and hour as last time — nobody has asked the course whether it is
          free.{' '}
          {rebooking.booking_url && (
            <a
              href={rebooking.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-fairway"
            >
              See that day&apos;s sheet ↗
            </a>
          )}
          {rebooking.booking_window_days != null && (
            <> The course books {rebooking.booking_window_days} days out.</>
          )}
        </p>
      </div>

      <div className="mb-3.5 flex gap-2.5">
        <div className="flex-1">
          <label htmlFor="rebook-holes" className={labelClass}>
            Holes
          </label>
          <select
            id="rebook-holes"
            value={draft.holes}
            onChange={(e) => set('holes', Number(e.target.value))}
            className={inputClass}
          >
            <option value={18}>18 holes</option>
            <option value={9}>9 holes</option>
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor="rebook-slots" className={labelClass}>
            Party
          </label>
          <select
            id="rebook-slots"
            value={draft.slots}
            onChange={(e) => set('slots', Number(e.target.value))}
            className={inputClass}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>
                {n} players
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-3.5">
        <label htmlFor="rebook-price" className={labelClass}>
          Green fee a head
        </label>
        <input
          id="rebook-price"
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={draft.price}
          onChange={(e) => set('price', e.target.value)}
          className={inputClass}
        />
        <p className="mt-1.5 text-[11px] text-ink-soft">
          {rebooking.price_cents_per_head != null
            ? `What it cost last time — the course's rate moves.`
            : `That outing recorded no rate, so there is nothing to carry over.`}
        </p>
      </div>

      <div className="mb-3.5 border-t border-sage-soft pt-3">
        <p className={labelClass}>The invitations</p>
        {draft.invites.length === 0 ? (
          <p className="mb-2 text-[12px] text-ink-soft">
            Nobody on this one yet — add whoever you want along.
          </p>
        ) : (
          <ul className="mb-2 flex flex-col">
            {draft.invites.map((invite) => (
              <li key={invite.contact} className="flex items-center gap-2 py-1 text-[13px] text-ink">
                <span className="min-w-0 truncate">{invite.contact}</span>
                {invite.previous_status && (
                  <span
                    className={cn(
                      'whitespace-nowrap text-[9.5px] font-semibold uppercase tracking-[0.14em]',
                      invite.previous_status === 'accepted' ? 'text-fairway' : 'text-brass-deep',
                    )}
                  >
                    {PREVIOUS_ANSWER[invite.previous_status]}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => dropInvite(invite.contact)}
                  aria-label={`Take ${invite.contact} off this invitation`}
                  className="ml-auto rounded-club-sm px-1 text-[15px] leading-none text-ink-soft hover:text-flag"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-2">
          <label htmlFor="rebook-add" className="sr-only">
            Add an email address or phone number
          </label>
          <input
            id="rebook-add"
            value={added}
            onChange={(e) => setAdded(e.target.value)}
            onKeyDown={(e) => {
              // Enter inside the add field must not submit the whole form —
              // proposing an outing is the one irreversible thing on this page.
              if (e.key !== 'Enter') return
              e.preventDefault()
              addInvite()
            }}
            placeholder="arnold@example.com"
            className={inputClass}
          />
          <ClubButton type="button" variant="ghost" size="sm" onClick={addInvite}>
            Add
          </ClubButton>
        </div>
      </div>

      {error && (
        <p role="alert" className="mb-3 text-[12.5px] text-flag">
          {error}
        </p>
      )}

      {/* The page's ONE flag CTA (golf doc §4). */}
      <ClubButton type="submit" size="sm" className="w-full" disabled={submitting}>
        {submitting ? 'Sending…' : 'Propose the outing'}
      </ClubButton>
      <button
        type="button"
        onClick={onCancel}
        className="mt-2.5 w-full rounded-club-sm text-[12.5px] text-ink-soft underline underline-offset-[3px] hover:text-fairway"
      >
        Never mind
      </button>
      <p className="mt-3 text-[11.5px] leading-relaxed text-ink-soft">
        Everyone is asked again from scratch — last time&apos;s answers are shown so you know who
        to expect, not carried over. Invitations go by email or by text, and no account is asked
        of anyone.
      </p>
    </form>
  )
}

// ── The screen ──────────────────────────────────────────────────────────────

/** Stats the page can state honestly, and none it cannot. */
function recordStats(rounds: HistoryRound[], outings: Outing[], viewerId: string | null): PageStat[] {
  const partners = new Set<string>()
  for (const round of rounds) {
    for (const player of round.players) {
      if (player.user_id != null && player.user_id === viewerId) continue
      partners.add(player.user_id ?? `guest:${player.display_name}`)
    }
  }

  const ownEighteens = rounds
    .filter((round) => round.status === 'completed' && round.holes_count === 18)
    .flatMap((round) => round.players)
    .filter((player) => player.user_id != null && player.user_id === viewerId)
    .map((player) => player.total_strokes)
    .filter((total): total is number => total != null)

  const stats: PageStat[] = [
    { value: String(rounds.length), label: rounds.length === 1 ? 'Round carded' : 'Rounds carded' },
    { value: String(outings.length), label: outings.length === 1 ? 'Outing kept' : 'Outings kept' },
    { value: String(partners.size), label: 'Playing partners' },
  ]
  // Only claimed where there is one. A best score of "—" is not a statistic.
  if (ownEighteens.length > 0) {
    stats.push({ value: String(Math.min(...ownEighteens)), label: 'Best eighteen' })
  }
  return stats
}

export function TheRecordScreen() {
  usePageTitle('The Record')
  const { isAuthenticated, user } = useAuth()
  const viewerId = user?.id ?? null

  const {
    rounds,
    isLoading: roundsLoading,
    error: roundsError,
    membershipRequired,
  } = useRoundHistory(isAuthenticated)
  const {
    outings,
    isLoading: outingsLoading,
    error: outingsError,
    create,
    rebooking,
  } = useOutings(isAuthenticated, 'past')

  const [openRoundId, setOpenRoundId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Rebooking | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [proposed, setProposed] = useState<string | null>(null)

  const outingsById = useMemo(
    () => new Map(outings.map((outing) => [outing.id, outing])),
    [outings],
  )

  const openRebooking = useCallback(
    async (outingId: string) => {
      setProposed(null)
      setDraftError(null)
      try {
        setDraft(await rebooking(outingId))
      } catch (e) {
        setDraftError(e instanceof Error ? e.message : 'Could not open that outing again.')
      }
    },
    [rebooking],
  )

  const propose = useCallback(
    async (body: Parameters<typeof create>[0]) => {
      const outing = await create(body)
      setDraft(null)
      setProposed(formatTeeTime(outing.tee_off_at))
    },
    [create],
  )

  const isLoading = roundsLoading || outingsLoading
  const error = roundsError ?? outingsError
  const stats = recordStats(rounds, outings, viewerId)

  return (
    <ClubPage chrome="clubhouse">
      <PageHead
        eyebrow="The Record"
        title="Every round,"
        emphasis="kept."
        lede="The golf you have actually played, the outings it came out of, and the shortest way to arrange the same again — a new invitation, drawn from an old one, with every line of it still yours to change."
        stats={isAuthenticated && !membershipRequired ? stats : undefined}
      />

      {!isAuthenticated ? (
        <section className="pb-16">
          <Container>
            <SignInPrompt what="your record — the rounds you have played, who played them with you, and the outings behind them" />
          </Container>
        </section>
      ) : (
        <section className="pb-16">
          <Container>
            {/* The gate sits on the DATA, never on the door: the page stays
                open so a lapsed member can always reach the account that fixes
                their dues. Same choice the clubhouse home makes. */}
            {membershipRequired ? (
              <EmptyPlate
                title="The record is kept for members in good standing."
                description="Your standing does not open your rounds just now. Your account page has the state of your dues, and the way to settle them."
                action={
                  <ClubLinkButton to="/clubhouse/account" variant="ghost" size="sm">
                    Your account
                  </ClubLinkButton>
                }
              />
            ) : (
              <div className="flex flex-wrap items-start gap-8">
                <div className="min-w-0 flex-1 basis-[520px]">
                  <AreaSectionHead eyebrow="Newest first" title="The rounds." />

                  {isLoading && <p className="text-[13.5px] text-ink-soft">Opening the record…</p>}
                  {error && (
                    <p role="alert" className="text-[13.5px] text-flag">
                      {error}
                    </p>
                  )}

                  {!isLoading && !error && rounds.length === 0 && (
                    <EmptyPlate
                      title="No rounds in your record yet."
                      description="The record opens with your first card. Nine holes or eighteen — it keeps whatever you enter, and invents nothing you didn't."
                      action={
                        <ClubLinkButton to="/clubhouse/outings" variant="ghost" size="sm">
                          Get a game together
                        </ClubLinkButton>
                      }
                    />
                  )}

                  {rounds.length > 0 && (
                    <ul className="flex flex-col gap-4">
                      {rounds.map((round) => (
                        <RoundEntry
                          key={round.id}
                          round={round}
                          outing={round.outing_id ? outingsById.get(round.outing_id) : undefined}
                          viewerId={viewerId}
                          open={round.id === openRoundId}
                          onToggle={() =>
                            setOpenRoundId(round.id === openRoundId ? null : round.id)
                          }
                          onRebook={openRebooking}
                        />
                      ))}
                    </ul>
                  )}

                  <div className="mt-12">
                    <AreaSectionHead eyebrow="Already teed off" title="The outings." />
                    {!isLoading && outings.length === 0 ? (
                      <EmptyPlate
                        title="No outings behind you yet."
                        description="Once a tee time you arranged has come and gone, it stays here — and one press puts the same group on the sheet again."
                      />
                    ) : (
                      <ul className="flex flex-col gap-3.5">
                        {outings.map((outing) => (
                          <PastOutingRow
                            key={outing.id}
                            outing={outing}
                            onRebook={openRebooking}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* The rail: the composer while one is open, and otherwise the
                    line that says what the button beside every round does. */}
                <aside
                  className="sticky top-24 flex-[0_0_clamp(240px,29vw,340px)] max-[860px]:static max-[860px]:flex-[1_1_100%]"
                  aria-label="Book it again"
                >
                  {draft ? (
                    <RebookComposer
                      key={draft.source_outing_id}
                      rebooking={draft}
                      onCancel={() => setDraft(null)}
                      onPropose={propose}
                    />
                  ) : (
                    <div className="rounded-[6px] border border-ink/12 bg-parchment p-5">
                      <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-brass-deep">
                        The same again
                      </p>
                      <h2 className="mt-1.5 font-display text-[20px] font-medium text-pine">
                        Book it again.
                      </h2>
                      {proposed ? (
                        <>
                          <p className="mt-2 text-[13px] text-fairway">
                            On the book for {proposed} — the invitations are out.
                          </p>
                          <ClubLinkButton
                            to="/clubhouse/outings"
                            variant="ghost"
                            size="sm"
                            className="mt-3.5"
                          >
                            Open the outings desk
                          </ClubLinkButton>
                        </>
                      ) : (
                        <p className="mt-2 text-[13px] text-ink-soft">
                          Every outing below opens again as a draft — the same course, the same
                          party, the same hour next week — and nothing is sent until you have
                          changed whatever needs changing.
                        </p>
                      )}
                      {draftError && (
                        <p role="alert" className="mt-3 text-[12.5px] text-flag">
                          {draftError}
                        </p>
                      )}
                    </div>
                  )}
                </aside>
              </div>
            )}
          </Container>
        </section>
      )}
    </ClubPage>
  )
}
